import React, { useEffect, useMemo, useRef, useState } from "react";
import { BottomBar, Button, ButtonGroup, CardsRenderer, DateRange, InlineFilterSelect, Pagination, Receipt, Searchbox, Spinner, Toast } from "../../../../../Components";
import moment from "moment";
import apis from "../../../../../Utils/apis";
import { get } from "../../../../../Utils/axios";
import ViewReceipt from "../../../../../Components/Receipt/ViewReceipt";
import SubmitReceipt from "./SubmitReceipt";
import AddReceipt from "./AddReceipt";
import { useClientSettings } from "../../../../../Hooks/useClientSettings";
import { useUserPreference } from "../../../../../Hooks/useUserPreference";
import { FiArrowRight } from "react-icons/fi";

function WithMe() {
	const DATE_FIELD_OPTIONS = [
		{ label: "Transaction Date", value: "transactionDate" },
		{ label: "Created Date", value: "createdDate" },
	];
	const getDefaultReceiptRange = () => ({
		startDate: moment().subtract(30, "days").startOf("day").valueOf(),
		endDate: moment().endOf("day").valueOf(),
	});
	const quickRanges = useMemo(
		() => [
			{ label: "Last 7 Days", value: 7 },
			{ label: "Last 30 Days", value: 30 },
			{ label: "Last 60 Days", value: 60 },
			{ label: "Last 90 Days", value: 90 },
		],
		[]
	);
	const RECEIPT_STATUS_MAP = {
		withMe: new Set(["draft", "inpolicy", "outofpolicy", "needsapproval"]),
		submitted: new Set(["approval", "pending", "processing"]),
		completed: new Set(["approved", "paid", "denied", "reimbursed"]),
	};
	const { isEnabled } = useClientSettings();
	const { showSidePane, sidePane, setReimbursementPaneEnabled, notificationsOverride } = useUserPreference();
	const expenseEnabled = isEnabled("ORGANIZATION") && isEnabled("EXPENSE");

	const [OpenId, setOpenId] = useState();
	const [Dragging, setDragging] = useState();
	const [QuickAddReceipt, setQuickAddReceipt] = useState();
	const [ForcePanelCloseTick, setForcePanelCloseTick] = useState(0);

	const [ReceiptStatus, setReceiptStatus] = useState({ label: "With Me", value: "withMe" });
	const [ActiveQuickRange, setActiveQuickRange] = useState(30);
	const initial = {
		receipts: [],
		count: 0,
		pages: 0,
	};
	const [Query, setQuery] = useState({
		search: "",
		page: 1,
		dateField: "transactionDate",
		dates: getDefaultReceiptRange(),
	});

	const [Receipts, setReceipts] = useState(initial);
	const [Loading, setLoading] = useState(true);
	const listTopRef = useRef(null);
	const serverItems = Receipts.receipts || [];
	const normalizeStatus = (status) => `${status || ""}`.replace(/\s+/g, "").toLowerCase();
	const allowedStatuses = RECEIPT_STATUS_MAP[ReceiptStatus.value] || null;
	const tabItems = allowedStatuses ? serverItems.filter((receipt) => allowedStatuses.has(normalizeStatus(receipt?.status))) : serverItems;
	const hasStatusMismatch = allowedStatuses ? tabItems.length !== serverItems.length : false;
	const hasServerCount = Number.isFinite(Number(Receipts.count)) && Number(Receipts.count) > 0;
	const totalCount = hasStatusMismatch ? tabItems.length : hasServerCount ? Number(Receipts.count) : tabItems.length;
	const totalPages = hasStatusMismatch
		? Math.max(1, Math.ceil(totalCount / 10))
		: Number.isFinite(Number(Receipts.pages)) && Number(Receipts.pages) > 0
			? Number(Receipts.pages)
			: Math.max(1, Math.ceil(totalCount / 10));
	const safePage = Math.min(Math.max(Query.page, 1), totalPages);
	const needsClientWindow = hasStatusMismatch;
	const visibleReceipts = needsClientWindow ? tabItems.slice((safePage - 1) * 10, safePage * 10) : tabItems;
	const currentItemsCount = visibleReceipts.length;
	const pageStart = currentItemsCount ? (safePage - 1) * 10 + 1 : 0;
	const pageEnd = currentItemsCount ? pageStart + currentItemsCount - 1 : 0;

	useEffect(() => {
		fetchReceipts(ReceiptStatus.value, Query);
	}, [ReceiptStatus, Query]);

	useEffect(() => {
		if (listTopRef.current) {
			listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}, [Query.page]);

	const fetchReceipts = (type, query) => {
		setLoading(true);
		let url;
		if (type === "withMe") {
			url = apis.getWithMeReceipts;
		} else if (type === "submitted") {
			url = apis.getPendingReceipts;
		} else if (type === "completed") {
			url = apis.getCompletedReceipts;
		}

		get(
			url,
			{
				...query,
				dates: {
					startDate: moment(query.dates.startDate).toDate(),
					endDate: moment(query.dates.endDate).toDate(),
				},
			},
			(r, e) => {
				if (r) {
					setReceipts(r);
					setLoading(false);
				} else if (e) {
					Toast.handleError(e);
					setLoading(false);
				}
			}
		);
	};

	const onQuickRangeChange = (option) => {
		const days = option.value;
		setActiveQuickRange(days);
		setQuery((prev) => ({
			...prev,
			page: 1,
			dates: {
				startDate: moment().subtract(days - 1, "days").startOf("day").valueOf(),
				endDate: moment().endOf("day").valueOf(),
			},
		}));
	};

	const handleReceiptCreated = () => {
		// Reset to the default With Me view so a newly created draft receipt is immediately visible.
		setReceiptStatus({ label: "With Me", value: "withMe" });
		setActiveQuickRange(30);
		setQuery((prev) => ({
			...prev,
			search: "",
			page: 1,
			dateField: "transactionDate",
			dates: getDefaultReceiptRange(),
		}));
	};

	return (
		<>
			{ReceiptStatus.value === "withMe" && <SubmitReceipt loading={Loading} dragging={Dragging} quickAddReceipt={QuickAddReceipt} forceCloseSignal={ForcePanelCloseTick} onReload={() => fetchReceipts(ReceiptStatus.value, Query)} />}
			<div ref={listTopRef} className="p-4 pb-28">
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					{expenseEnabled && (
						<div className="w-full overflow-x-auto scrollbar-hidden sm:w-auto">
							<ButtonGroup
								onClick={(status) => {
									setReceiptStatus(status);
									setQuery((prev) => ({ ...prev, page: 1 }));
								}}
								size="xs !p-1.5"
								selected={ReceiptStatus}
								className="text-sm whitespace-nowrap"
								options={[
									{ label: "With Me", value: "withMe" },
									{ label: "Submitted", value: "submitted" },
									{ label: "Completed", value: "completed" },
								]}
							/>
						</div>
					)}
					<div className="flex w-full flex-col gap-2 lg:flex-row lg:items-center lg:gap-x-2 sm:w-auto">
						<Searchbox className="w-full lg:flex-1 xl:w-96" placeholder="Search receipts" cmd="s" onChange={(search) => setQuery((prev) => ({ ...prev, search, page: 1 }))} />
						<div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)] lg:w-[26rem] xl:w-auto">
							<InlineFilterSelect
								value={Query.dateField}
								options={DATE_FIELD_OPTIONS}
								onChange={(option) =>
									setQuery((prev) => ({
										...prev,
										page: 1,
										dateField: option.value,
									}))
								}
							/>
							<DateRange
								backDateSelection
								maxDate={moment().endOf("day").valueOf()}
								setRange={(dates) => {
									setActiveQuickRange(null);
									setQuery((prev) => ({ ...prev, dates, page: 1 }));
								}}
								range={Query.dates}
								className="w-full"
								inputClass="!p-1.5 !text-xs w-full"
							/>
						</div>
						{/* <ExpenseFilters /> */}
					</div>
				</div>
				{ReceiptStatus.value === "completed" && (
					<div className="mt-3 overflow-x-auto scrollbar-hidden">
						<ButtonGroup options={quickRanges} selected={quickRanges.find((r) => r.value === ActiveQuickRange)} onClick={onQuickRangeChange} className="text-xs whitespace-nowrap min-w-max" />
					</div>
				)}
				{ReceiptStatus.value === "withMe" && (
					<div className="mt-3 p-3 border rounded-xl bg-light border-surface flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
						<div className="text-xs text-secondary">
							1) Add/upload receipt 2) Click <span className="font-semibold text-dark">Move to Reimbursement</span> on an eligible card (or drag on desktop) 3) Click <span className="font-semibold text-dark">Send for Reimbursement</span>
						</div>
					<Button
						onClick={() => {
							setReimbursementPaneEnabled(true);
							if (sidePane && !notificationsOverride) {
								setForcePanelCloseTick((tick) => tick + 1);
								return;
							}
							showSidePane(true);
						}}
						variant={sidePane && !notificationsOverride ? "light" : "primary"}
						className="btn-sm gap-2 w-full sm:w-auto"
					>
						{sidePane && !notificationsOverride ? "Hide Reimbursement Panel" : "Open Reimbursement Panel"} <FiArrowRight />
					</Button>
					</div>
				)}
				<div className="mt-4">
					{Loading ? (
						<div className="flex-center py-12">
							<Spinner />
						</div>
					) : (
						<div className="space-y-4">
							<CardsRenderer
								items={visibleReceipts}
								render={(receipt, i) => (
									<Receipt
										onReload={() => fetchReceipts(ReceiptStatus.value, Query)}
										onDrag={setDragging}
										onAddToReimbursement={() => {
													setReimbursementPaneEnabled(true);
											showSidePane(true);
											setQuickAddReceipt({
												...receipt,
												__quickAddKey: `${receipt.id || "receipt"}-${Date.now()}`,
											});
										}}
										key={i}
										{...receipt}
									/>
								)}
							/>
						</div>
					)}
				</div>
				<ViewReceipt onReload={() => fetchReceipts(ReceiptStatus.value, Query)} receiptId={OpenId} onClose={() => setOpenId()} />
			</div>
			<BottomBar>
				<div className="flex flex-col w-full gap-1.5">
					<div className="flex items-end justify-between gap-2">
						<div className="align-center gap-x-4">
							<AddReceipt onReload={() => fetchReceipts(ReceiptStatus.value, Query)} onCreated={handleReceiptCreated} />
							<h5>
								{totalCount} Receipt {ReceiptStatus.label}
							</h5>
						</div>
						<div className="text-xs text-secondary whitespace-nowrap">
							Showing {pageStart}-{pageEnd}
							{Number.isFinite(Number(Receipts.count)) && Number(Receipts.count) > 0 ? ` of ${totalCount}` : ""}
						</div>
					</div>
					<Pagination showNumbers={false} active={safePage} total={totalPages} onChange={(page) => setQuery({ ...Query, page })} />
				</div>
			</BottomBar>
		</>
	);
}

export default WithMe;
